import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "@/utils/useAxios";
import { editTaskDTO } from "./editTaskModal";
import {
  IMerchant,
  IMerchantUsers,
  IOverview,
  IPwtTransactions,
  ITerminal,
  ITerminalHealth,
  ITransaction,
  ITransactionCycles,
} from "./types";
import { downloadCSV, errorLogger } from "@/utils/helper";
import { toast } from "react-toastify";

export interface TaskOverview {
  _id: string;
  count: number;
}
export interface Task {
  data: ITask[];
  pagination: Pagination;
}

export interface ITask {
  _id: string;
  title: string;
  statusTracker: StatusTracker[];
  status: string;
  description: string;
  user_id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface StatusTracker {
  status: string;
  updateAt: string;
  _id: string;
}

export interface Pagination {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const useGetAllTransaction = (filter?: {
  page: number;
  responseCode?: string;
  merchantName?: string;
  cardtype?: string;
  paymentType?: string;
  rrn?: string;
  stan?: string;
  search?: string;
  limit?: number;
  terminalId?: string;
  type?: string;
  merchantId?: string;
  transactionId?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ["use_fetch_tx", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/admin/transactions/all", {
          params: {
            ...filter,
          },
        });
        return response.data.data as ITransaction;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllPwtTransaction = (filter?: {
  size: number;
  page: number;
}) => {
  return useQuery({
    queryKey: ["use_fetch_ptx", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/pwt/notifications", {
          params: {
            ...filter,
          },
        });
        return response.data.data as IPwtTransactions;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetDashboardOverview = (filters?: {
  merchantId?: string;
  merchantName?: string;
}) => {
  return useQuery({
    queryKey: ["use_fetch_overview", filters],
    queryFn: async () => {
      try {
        const response = await axios.get("/admin/dashboard/overview", {
          params: filters,
        });
        return response.data.data as IOverview;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetEJournal = ({ terminalId, stan }: any) => {
  return useQuery({
    queryKey: ["use_ejournal"],
    queryFn: async () => {
      try {
        const response = await axios.post("/admin/transaction/ejournal", {
          terminalId: terminalId,
          stan: stan,
        });
        return response.data.data as any;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
    enabled: false,
  });
};

//=========================Merchant==========================
export const useGetAllMerchant = (filter?: { size: number; page: number }) => {
  return useQuery({
    queryKey: ["use_fetch_merchant", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/merchants", {
          params: {
            ...filter,
          },
        });
        return response.data.data as IMerchant;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetCardTransactionsCount = () => {
  return useQuery({
    queryKey: ["get-card-transaction-count"],
    queryFn: async () => {
      try {
        const response = await axios.get("/transactions/count", {});
        return response.data.data;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetPwtTransactionCount = () => {
  return useQuery({
    queryKey: ["get-pwt-transaction-count"],
    queryFn: async () => {
      try {
        const response = await axios.get("/pwt/count", {});
        return response.data.data;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllBanks = () => {
  return useQuery({
    queryKey: ["use_fetch_banks"],
    queryFn: async () => {
      try {
        const response = await axios.get("/admin/banks");
        return response.data.data as any;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetSettlementCycle = () => {
  return useQuery({
    queryKey: ["use_get_settlement_cycle"],
    queryFn: async () => {
      try {
        const response = await axios.get("/settlements/cycle");
        return response.data.data as ITransactionCycles;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllMerchantUsers = (filter?: {
  size: number;
  page: number;

}) => {
  return useQuery({
    queryKey: ["get_merchant_users", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/admin/clientmerchants", {
          params: {
            ...filter,
          },
        });
        return response.data.data as IMerchantUsers;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllTerminal = (filter?: { size: number; page: number }) => {
  return useQuery({
    queryKey: ["use_fetch_terminal", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/admin/terminals/all", {
          params: {
            ...filter,
          },
        });
        return response.data.data as ITerminal;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllTerminalHealth = (filter?: {
  size: number;
  page: number;
}) => {
  return useQuery({
    queryKey: ["get_terminal_health", { ...filter }],
    queryFn: async () => {
      try {
        const response = await axios.get("/healthinfo/all", {
          params: {
            ...filter,
          },
        });
        return response.data.data as ITerminalHealth;
      } catch (error) {
        errorLogger(error);
      }
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useGetMerchantTerminal = ({
  merchantId,
}: {
  merchantId: string;
}) => {
  return useMutation({
    mutationFn: async () => {
      try {
        const response = await axios.get("/terminals/merchant/" + merchantId);
        return response.data.data as any;
      } catch (error) {
        errorLogger(error);
      }
    },
  });
};

export const useCreateMerchant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-merchant"],
    mutationFn: async (payload: any) => {
      const response = await axios.post("/merchants/create", payload);
      return response.data.data as any;
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_merchant"],
      });
    },
  });
};

export const useUpdateMerchant = ({
  id,
  onSuccess,
}: {
  id: string;
  onSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-merchant"],
    mutationFn: async (payload: any) => {
      const response = await axios.put("/merchants/" + id, payload);
      return response.data.data as any;
    },
    onSuccess() {
      onSuccess();
      toast.success("Merchant Updated Successfully");
    },
    onError() {
      toast.error("Merchant Update Failed, try again");
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_merchant"],
      });
    },
  });
};

export const useInviteMerchantUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["invite-user"],
    mutationFn: async (payload: any) => {
      const response = await axios.post(
        "/admin/clientmerchants/invite",
        payload
      );
      return response.data.data as any;
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["get_merchant_users"],
      });
    },
  });
};

export const useDeleteMerchantUser = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-user"],
    mutationFn: async (payload: any) => {
      const response = await axios.delete(
        "/admin/clientmerchant/" + payload.id
      );
      return response.data.data as any;
    },
    onSuccess(data, variables, context) {
      toast.success("Merchant User Deleted Successfully");
      onSuccess();
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["get_merchant_users"],
      });
    },
  });
};

export const useDeleteMerchant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-merchant"],
    mutationFn: async (payload: any) => {
      const response = await axios.delete("/admin/merchant/" + payload.id);
      return response.data.data as any;
    },
    onSuccess() {
      toast.success("Merchant Deleted Successfully");
    },
    onError() {
      toast.error("Merchant Deletion Failed, try again");
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_merchant"],
      });
    },
  });
};

export const useCreateTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-terminal"],
    mutationFn: async (payload: any) => {
      const response = await axios.post("/terminals/create", payload);
      return response.data.data as any;
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_terminal"],
      });
    },
  });
};

export const useDeleteTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-terminal"],
    mutationFn: async (payload: any) => {
      const response = await axios.delete("/terminals/" + payload.id);
      return response.data.data as any;
    },
    onSuccess() {
      toast.success("Terminal Deleted Successfully");
    },
    onError() {
      toast.error("Terminal Deletion Failed, try again");
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_terminal"],
      });
    },
  });
};

export const useUpdateTerminal = ({ id }: { id: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-terminal"],
    mutationFn: async (payload: any) => {
      const response = await axios.put("/terminals/" + id, payload);
      return response.data.data as any;
    },
    onSuccess() {
      toast.success("Terminal Updated Successfully");
    },
    onError() {
      toast.error("Terminal Update Failed, try again");
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_terminal"],
      });
    },
  });
};

export const useEditTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["edit-task"],
    mutationFn: async ({
      payload,
      id,
    }: {
      payload: editTaskDTO;
      id: string;
    }) => {
      const response = await axios.patch("/task/" + id, payload);
      return response.data.data as any;
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_task"],
      });
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_task_overview"],
      });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["delete-task"],
    mutationFn: async (id: string) => {
      const response = await axios.delete("/task/" + id);
      return response.data.data as any;
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_task"],
      });
      queryClient.invalidateQueries({
        queryKey: ["use_fetch_task_overview"],
      });
    },
  });
};

export const useDownloadTransactionReciept = ({
  id,
  onSuccess,
}: {
  id: string;
  onSuccess: (data: any) => void;
}) => {
  return useMutation({
    mutationFn: async () => {
      try {
        const response = await axios.get("/admin/download/transact/" + id);
        return response.data;
      } catch (error) {
        errorLogger(error);
      }
    },
    onSuccess: (data) => {
      onSuccess(data);
    },
  });
};

export const useSendTransactionReportToEmail = () => {
  return useMutation({
    mutationFn: async ({
      data,
      filter,
    }: {
      data: { email: string };
      filter: {
        fromDate: string;
        toDate: string;
        merchantId: string;
        terminalId: string;
      };
    }) => {
      try {
        const response = await axios.post(
          "/admin/send-transaction-report",
          data,
          {
            params: {
              ...filter,
            },
          }
        );
        return response.data;
      } catch (error) {
        errorLogger(error);
      }
    },
    onSuccess: (data) => {
      // onSuccess(data);
    },
  });
};

export const useSendPwtTransactionReportToEmail = () => {
  return useMutation({
    mutationFn: async ({
      data,
      filter,
    }: {
      data: { email: string };
      filter: {
        fromDate: string;
        toDate: string;
        merchantId: string;
        terminalId: string;
      };
    }) => {
      try {
        const response = await axios.post("/pwt/send-report", data, {
          params: {
            ...filter,
          },
        });
        return response.data;
      } catch (error) {
        errorLogger(error);
      }
    },
    onSuccess: (data) => {
      // onSuccess(data);
    },
  });
};

export const useDownloadTransactionReport = ({
  filter,
  onSuccess,
}: {
  filter: {
    fromDate: string;
    toDate: string;
    merchantId: string;
    terminalId: string;
  };
  onSuccess: () => void;
}) => {
  return useQuery({
    queryKey: [
      "download_transaction_report",
      {
        params: {
          ...filter,
        },
      },
    ],
    queryFn: async () => {
      try {
        const response = await axios.get(
          "/admin/download/transactions/report",
          {
            params: {
              ...filter,
            },
            // responseType: "blob",
          }
        );
        downloadCSV(response?.data?.data, "card-transaction-report");
        onSuccess();
        return response;
      } catch (error) {
        errorLogger(error);
      }
    },
    enabled: false,
  });
};

export const useDownloadPwtTransactionReport = ({
  filter,
  onSuccess,
}: {
  filter: {
    fromDate: string;
    toDate: string;
    merchantId: string;
    terminalId: string;
  };
  onSuccess: () => void;
}) => {
  return useQuery({
    queryKey: [
      "download_pwt_transaction_report",
      {
        params: {
          ...filter,
        },
      },
    ],
    queryFn: async () => {
      try {
        const response = await axios.get("/pwt/download/report", {
          params: {
            ...filter,
          },
          // responseType: "blob",
        });
        downloadCSV(response?.data?.data, "card-transaction-report");
        onSuccess();
        return response;
      } catch (error) {
        errorLogger(error);
      }
    },
    enabled: false,
  });
};

export const useGetPWTCount = () => {
  return useQuery({
    queryKey: ["pwt_transaction_count"],
    queryFn: async () => {
      try {
        const response = await axios.get("/pwt/count");
        return response;
      } catch (error) {
        errorLogger(error);
      }
    },
  });
};
